import { useEffect, useState } from 'react';
import { Pencil, Check } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { SELECT_CLASS } from './ElementEditModal';
import { cn } from '../../lib/utils';
import {
  COLOR_PALETTE, LINE_WEIGHTS, ARROW_TYPES, HOSE_TYPES, DEFAULT_ARROW_TYPE, DEFAULT_HOSE_TYPE,
  resolveLineStyle, visibleLabel,
} from '../../lib/lineStyles';

const TITLES = { line: 'Linie', arrow: 'Pfeil', hose: 'Schlauchleitung', polygon: 'Fläche', circle: 'Kreis' };

// Bearbeiten von Linien, Pfeilen, Schlauchleitungen, Flächen und Kreisen
export const ShapeEditModal = ({ open, onOpenChange, element, onSave }) => {
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('');
  const [dashed, setDashed] = useState(false);
  const [weight, setWeight] = useState(3);
  const [arrowType, setArrowType] = useState(DEFAULT_ARROW_TYPE);
  const [hoseType, setHoseType] = useState(DEFAULT_HOSE_TYPE);
  const [radius, setRadius] = useState(100);

  const kind = element?.type === 'polyline' ? (element.properties?.kind || 'line') : element?.type;
  const isLine = element?.type === 'polyline';

  useEffect(() => {
    if (!open || !element) return;
    const style = resolveLineStyle(element.properties);
    setLabel(visibleLabel(element.properties?.label));
    setColor(style.color);
    setDashed(style.dashed);
    setWeight(style.weight);
    setArrowType(element.properties?.arrowType || DEFAULT_ARROW_TYPE);
    setHoseType(element.properties?.hoseType || DEFAULT_HOSE_TYPE);
    setRadius(Math.round(element.radius || 100));
  }, [open, element]);

  // Neue Pfeilart übernimmt deren Farbe und Linienart
  const changeArrowType = (type) => {
    setArrowType(type);
    setColor(ARROW_TYPES[type].color);
    setDashed(ARROW_TYPES[type].dashed);
  };

  const handleSave = () => {
    const properties = { label: label.trim(), color, dashed };
    if (isLine) properties.weight = weight;
    if (kind === 'arrow') properties.arrowType = arrowType;
    if (kind === 'hose') properties.hoseType = hoseType;
    const patch = { properties };
    if (kind === 'circle') patch.radius = Math.max(1, Number(radius) || 100);
    onSave(patch);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="shape-edit-modal">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wider flex items-center gap-2 text-lunefire-charcoal dark:text-white">
            <Pencil className="w-5 h-5 text-lunefire-red" />
            {TITLES[kind] || 'Element'} bearbeiten
          </DialogTitle>
          <DialogDescription className="font-body">
            Die Bezeichnung wird auf der Karte angezeigt.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4 py-2" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          {kind === 'arrow' && (
            <div className="space-y-2">
              <Label htmlFor="shape-arrow-type">Pfeilart</Label>
              <select
                id="shape-arrow-type"
                value={arrowType}
                onChange={(e) => changeArrowType(e.target.value)}
                className={SELECT_CLASS}
                data-testid="shape-arrow-type"
              >
                {Object.entries(ARROW_TYPES).map(([key, type]) => (
                  <option key={key} value={key}>{type.label}</option>
                ))}
              </select>
            </div>
          )}

          {kind === 'hose' && (
            <div className="space-y-2">
              <Label htmlFor="shape-hose-type">Schlauchtyp</Label>
              <select
                id="shape-hose-type"
                value={hoseType}
                onChange={(e) => setHoseType(e.target.value)}
                className={SELECT_CLASS}
                data-testid="shape-hose-type"
              >
                {Object.entries(HOSE_TYPES).map(([key, type]) => (
                  <option key={key} value={key}>{type.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="shape-label">Bezeichnung</Label>
            <Input
              id="shape-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={kind === 'arrow' ? ARROW_TYPES[arrowType].label : 'optional'}
              maxLength={60}
              data-testid="shape-label-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Farbe</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PALETTE.map(({ name, value }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setColor(value)}
                  title={name}
                  aria-label={name}
                  aria-pressed={color === value}
                  className={cn(
                    "w-8 h-8 touch:w-10 touch:h-10 rounded-full border-2 flex items-center justify-center",
                    color === value ? "border-foreground" : "border-transparent"
                  )}
                  style={{ background: value }}
                  data-testid={`shape-color-${name.toLowerCase()}`}
                >
                  {color === value && <Check className="w-4 h-4 text-white" />}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Linienart</Label>
            <div className="flex gap-2">
              {[[false, 'Durchgezogen'], [true, 'Gestrichelt']].map(([value, text]) => (
                <Button
                  key={text}
                  type="button"
                  variant={dashed === value ? 'secondary' : 'outline'}
                  className={cn("flex-1 touch:h-11", dashed === value && "border border-lunefire-red/40")}
                  onClick={() => setDashed(value)}
                >
                  {text}
                </Button>
              ))}
            </div>
          </div>

          {isLine && (
            <div className="space-y-2">
              <Label>Stärke</Label>
              <div className="flex gap-2">
                {LINE_WEIGHTS.map(({ key, label: text, value }) => (
                  <Button
                    key={key}
                    type="button"
                    variant={weight === value ? 'secondary' : 'outline'}
                    className={cn("flex-1 touch:h-11", weight === value && "border border-lunefire-red/40")}
                    onClick={() => setWeight(value)}
                    data-testid={`shape-weight-${key}`}
                  >
                    {text}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {kind === 'circle' && (
            <div className="space-y-2">
              <Label htmlFor="shape-radius">Radius (m)</Label>
              <Input
                id="shape-radius"
                type="number"
                min={1}
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                data-testid="shape-radius-input"
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              className="bg-lunefire-red hover:bg-lunefire-red/90 text-white font-display uppercase tracking-wider"
              data-testid="shape-save-btn"
            >
              Übernehmen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ShapeEditModal;
